import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Sparkles, Download, Loader2, CheckCircle2, Brain, TrendingUp, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReleaseNotesMiner = ({ project }) => {
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [analysisResults, setAnalysisResults] = useState({});

  useEffect(() => {
    fetchReleaseNotes();
  }, []);

  const fetchReleaseNotes = async () => {
    try {
      const response = await axios.get(`${API}/release-notes`);
      setReleaseNotes(response.data);
    } catch (error) {
      console.error('Error fetching release notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockNotes = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/mock-data/release-notes`);
      await fetchReleaseNotes();
    } catch (error) {
      console.error('Error generating release notes:', error);
    } finally {
      setGenerating(false);
    }
  };

  const analyzeNote = async (note) => {
    setAnalyzing(note.id);
    try {
      const response = await axios.post(`${API}/ai/analyze-release-notes`, {
        project_id: project.id,
        analysis_type: 'release_notes',
        context: {
          release_note: note,
          project_context: {
            source_system: project.source_system,
            target_system: project.target_system,
            modules: ['FI', 'CO', 'SD', 'MM'],
            migration_type: project.migration_type
          }
        }
      });
      
      // Store the analysis result
      setAnalysisResults({
        ...analysisResults,
        [note.id]: response.data.result.ai_summary
      });
      
    } catch (error) {
      console.error('Error analyzing release note:', error);
      alert('Failed to analyze release note');
    } finally {
      setAnalyzing(null);
    }
  };

  const categoryColors = {
    bug_fix: 'bg-green-100 text-green-800',
    feature: 'bg-blue-100 text-blue-800',
    security: 'bg-red-100 text-red-800',
    performance: 'bg-purple-100 text-purple-800'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600 to-pink-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <FileText className="w-12 h-12" />
            <div>
              <h2 className="text-3xl font-bold">Release Notes Miner</h2>
              <p className="text-orange-100 mt-1">AI-powered analysis of SAP release notes for project relevance</p>
            </div>
          </div>
          {releaseNotes.length === 0 && (
            <button
              onClick={generateMockNotes}
              disabled={generating}
              className="px-6 py-3 bg-white text-orange-700 rounded-xl hover:bg-orange-50 transition-colors font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Generate Sample Notes</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {releaseNotes.map((note) => {
          const hasAnalysis = analysisResults[note.id];
          
          return (
            <div
              key={note.id}
              className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="font-mono text-sm font-bold bg-gray-900 text-white px-3 py-1.5 rounded-lg">
                      {note.note_number}
                    </span>
                    <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${categoryColors[note.category]}`}>
                      {note.category.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-semibold">
                      {note.release_version}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{note.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{note.content}</p>
                  <div className="flex items-center space-x-6 text-sm">
                    <div>
                      <span className="text-gray-600 font-medium">Impact Areas: </span>
                      <span className="text-gray-900 font-semibold">
                        {note.impact_areas.join(', ')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Published: </span>
                      <span className="text-gray-900 font-semibold">
                        {new Date(note.published_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => analyzeNote(note)}
                  disabled={analyzing === note.id}
                  className="ml-4 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
                >
                  {analyzing === note.id ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : hasAnalysis ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Re-analyze</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>AI Analyze</span>
                    </>
                  )}
                </button>
              </div>

              {hasAnalysis && (
                <div className="mt-6 pt-6 border-t-2 border-gray-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <Brain className="w-6 h-6 text-purple-600" />
                    <h4 className="text-lg font-bold text-gray-900">AI Analysis Results</h4>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                      {analysisResults[note.id]}
                    </pre>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-green-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-semibold">Analysis Complete</span>
                    </div>
                    <button
                      onClick={() => {
                        const updatedResults = { ...analysisResults };
                        delete updatedResults[note.id];
                        setAnalysisResults(updatedResults);
                      }}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
                    >
                      Clear Analysis
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {releaseNotes.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
          <FileText className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No release notes available</h3>
          <p className="text-gray-600 mb-6">Generate sample SAP release notes to get started</p>
        </div>
      )}
    </div>
  );
};

